import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  api,
  type MCPObservabilityReport,
} from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";

function resultClassStyle(cls: string): string {
  switch (cls) {
    case "success":
      return "text-emerald-700";
    case "denied":
      return "text-amber-700";
    case "failure":
      return "text-error";
    default:
      return "text-text-muted";
  }
}

export function ObservabilityMCPPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [data, setData] = useState<MCPObservabilityReport | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const { data: rep } = await api.mcpObservability(id, { limit: 50 });
        setData(rep);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading MCP observability…" />;

  const stats = data?.stats;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="MCP"
        description="Tool-call volume, success vs denied/failure, and recent agent activity for this project."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {!data || !stats ? (
        <p className="text-sm text-text-muted">No MCP activity yet.</p>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Total calls"
              value={stats.total_calls.toLocaleString()}
            />
            <StatTile
              label="Success"
              value={stats.success_count.toLocaleString()}
            />
            <StatTile
              label="Denied"
              value={stats.denied_count.toLocaleString()}
              hint="insufficient scope"
            />
            <StatTile
              label="Failures"
              value={stats.failure_count.toLocaleString()}
            />
          </div>

          {stats.by_tool.length > 0 && (
            <div className="card p-4">
              <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">
                By tool
              </h3>
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th className="text-right">Calls</th>
                    <th className="text-right">OK</th>
                    <th className="text-right">Denied</th>
                    <th className="text-right">Fail</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.by_tool.map((t) => (
                    <tr key={t.tool_name}>
                      <td className="font-mono text-xs">{t.tool_name}</td>
                      <td className="text-right tabular-nums">{t.calls}</td>
                      <td className="text-right tabular-nums">{t.success}</td>
                      <td className="text-right tabular-nums">{t.denied}</td>
                      <td className="text-right tabular-nums">{t.failure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card p-4">
            <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">
              Recent tool calls
            </h3>
            {data.events.length === 0 ? (
              <p className="text-sm text-text-muted">
                No tools/call events in this window.
              </p>
            ) : (
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Tool</th>
                    <th>Result</th>
                    <th>Client</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e) => (
                    <tr key={e.id}>
                      <td className="text-xs text-text-muted whitespace-nowrap">
                        {new Date(e.occurred_at).toLocaleString()}
                      </td>
                      <td className="font-mono text-xs">
                        {e.tool_name || "—"}
                      </td>
                      <td>
                        <span
                          className={`mono-chip ${resultClassStyle(e.result_class)}`}
                        >
                          {e.result_class}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-text-muted">
                        {e.client_id
                          ? e.client_id.length > 12
                            ? `${e.client_id.slice(0, 8)}…`
                            : e.client_id
                          : "—"}
                      </td>
                      <td className="text-xs text-text-muted max-w-[14rem] truncate">
                        {e.detail || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
